import ExamImage from "../components/ExamImage";
import { useEffect, useRef, useState } from "react";
import QuestionLayout from "./QuestionLayout";
import AudioPlayButton from "./AudioPlayButton";
import { melodyInput } from "./notation/melodyInput";
import KeySignatureInputWidget from "./KeySignatureInput";

export default function MelodyEditorWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const containerRef = useRef(null);
  const engineRef = useRef(null);

  // 🔒 stable audio identity
  const audioRef = useRef(value._audio || {});

  // 🔒 non-react storage for notes
  const notesRef = useRef(null);

  // 🔒 ensure we only load once per question
  const hasLoadedRef = useRef(false);

  const audioValue = audioRef.current;

  /* ─────────────────────────────────────────────
     INIT ENGINE (once)
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!containerRef.current) return;

    engineRef.current = new melodyInput(
      "melody-editor" + config.number,
      containerRef.current,
      {
        measures: config.measures,
        timeSig: config.timeSig,
        clef: config.clef,
        lockPitch: config.lockPitch,
        lockRhythm: config.lockRhythm,
        fixedPitch: config.fixedPitch,
        keyText: config.keyText,
        keyTextOffset: config.keyTextOffset,
        disabledMeasures: config.disabledMeasures || []
      },
      {
        // 🔑 ONLY emit changes in edit mode
        setNotes: notes => {
          if (mode !== "edit") return;

          const frozenNotes = structuredClone(notes);
          notesRef.current = frozenNotes;

          onChange({
            ...value,
            notes: frozenNotes,
            _audio: audioRef.current
          });
        }
      }
    );

    return () => {
      engineRef.current = null;
      hasLoadedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─────────────────────────────────────────────
     AUTHORITATIVE LOAD (ONCE)
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!engineRef.current) return;
    if (hasLoadedRef.current) return;

    // 1️⃣ Load structural config
    engineRef.current.load(config);

    // 2️⃣ Inject student melody ONCE
    if (value?.notes) {
      engineRef.current.load({
        ...config,
        melody: value.notes
      });
      notesRef.current = value.notes;
    }

    if (config.lockRhythm) engineRef.current.hideDurButton();

    hasLoadedRef.current = true;
  }, [config, value?.notes]);

  /* ─────────────────────────────────────────────
     ENABLE / DISABLE ENGINE BY MODE
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!engineRef.current) return;

    // 👇 pick the one your engine supports
    if (mode==="review") engineRef.current.disabled = true;
  }, [mode]);

  /* ─────────────────────────────────────────────
     AUDIO STATE UPDATE
  ───────────────────────────────────────────── */

  function setAudioState(audioState) {
    if (mode !== "edit") return;

    audioRef.current = audioState;

    onChange({
      ...value,
      _audio: audioState
    });
  }

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
      horizontalScrollRef={containerRef}
    >
      {/* AUDIO */}
      {config.audio && (
        <div style={{ marginBottom: 12 }}>
          <AudioPlayButton
            src={config.audio}
            value={audioValue}
            onChange={setAudioState}
            mode={mode}
            maxListens={config.maxListens ?? 1}
          />
        </div>
      )}

      {config.image && (
        <div>
          <ExamImage width={1000} src={config.image}></ExamImage>
        </div>
      )}

      {/* NOTATION EDITOR */}
      <div
        ref={containerRef}
        id={"melody-editor" + config.number}
        style={{
          padding: 10,
          borderRadius: 6,
          maxWidth: 1100,
          overflow: "auto"
        }}
      />
    </QuestionLayout>
  );
}


